import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import AR from "@/i18n/dictionary";
import { startTranslator, stopTranslator } from "@/i18n/domTranslator";

const STORAGE_KEY = "hah_locale";
const SUPPORTED = ["en", "ar"];

const I18nContext = createContext({
  locale: "en",
  setLocale: () => {},
  t: (s) => s,
  isRTL: false,
});

function applyHtmlAttrs(locale) {
  const dir = locale === "ar" ? "rtl" : "ltr";
  document.documentElement.setAttribute("lang", locale);
  document.documentElement.setAttribute("dir", dir);
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED.includes(saved)) return saved;
    } catch (_e) { /* ignore storage errors */ }
    return "en";
  });

  useEffect(() => {
    applyHtmlAttrs(locale);
    if (locale === "ar") startTranslator(AR);
    else stopTranslator();
    return () => stopTranslator();
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* ignore */ }
    setLocaleState(next);
  }, []);

  const t = useCallback((key) => {
    if (locale === "ar" && key && AR[key]) return AR[key];
    return key;
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    isRTL: locale === "ar",
  }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Convenience hook: just the translator function
export function useT() {
  return useContext(I18nContext).t;
}
